import TFloatingPopover from 'components/popovers/TFloatingPopover'
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { LocalStorageKey } from 'types/services/localStorage'

type PopoverContextType = {
  openPopover: (
    eventId: string,
    content: React.ReactNode,
    preventClose?: boolean,
    onOutsideClick?: () => void,
    elementPointTo?: HTMLElement
  ) => void
  closePopover: (withCache?: boolean) => void
  currentEventId: string | null
  setPreventClose: (value: boolean) => void
  setOnOutsideClick: (callback: () => void) => void // New method to set onOutsideClick dynamically
  pointTo: (el: HTMLElement | null, eventId: string) => void
}

const EventPopoverContext = createContext<PopoverContextType>({
  openPopover: () => {},
  closePopover: () => {},
  currentEventId: null,
  setPreventClose: () => {},
  setOnOutsideClick: () => {},
  pointTo: (el: HTMLElement | null, eventId: string) => {}
})

export const EventPopoverProvider = ({
  children
}: {
  children: React.ReactNode
}) => {
  const [state, setState] = useState<{
    eventId: string | null
    anchorEl: HTMLElement | null
    content: React.ReactNode
    preventClose: boolean
    onOutsideClick?: () => void
    lastPosition?: { x: number; y: number }
  }>({
    eventId: null,
    anchorEl: null,
    content: null,
    preventClose: false,
    onOutsideClick: undefined,
    lastPosition: undefined
  })

  const value = useMemo(
    () => ({
      pointTo: (el: HTMLElement | null, eventId: string) => {
        if (el && state.content) {
          Logger.orange('EventPopoverProvider:pointTo', el)
          // Trigger click on all child elements

          // Check if the child with id="event-root" exists
          const targetChild = el.querySelector('#event-root')

          Logger.orange('EventPopoverProvider:targetChild', targetChild)

          if (state.content) {
            if (targetChild instanceof HTMLElement) {
              setTimeout(() => {
                targetChild.click() // Trigger click on the specific child
              }, 150)
            } else {
              setTimeout(() => {
                el.click()
              }, 150)
            }
          }
        }
      },
      openPopover: (
        eventId: string,
        content: React.ReactNode,
        preventClose: boolean = false,
        onOutsideClick?: () => void,
        elementPointTo?: HTMLElement
      ) => {
        const element = elementPointTo ?? document.getElementById(eventId)
        Logger.orange('EventPopoverProvider:element', element)
        if (state.anchorEl?.id === element?.id) {
          return
        }
        setState({
          eventId,
          anchorEl: element,
          content,
          preventClose,
          onOutsideClick
        })
      },
      closePopover: (withCache?: boolean) => {
        if (LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP) === '1') return

        LocalStorage.remove(LocalStorageKey.VISIBLE_EVENT)
        setState((prev) => ({
          ...prev,
          anchorEl: null,

          eventId: null,
          onOutsideClick: undefined
        }))
      },
      currentEventId: state.eventId,
      setPreventClose: (value: boolean) => {
        setState((prev) => ({ ...prev, preventClose: value }))
      },
      setOnOutsideClick: (callback: () => void) => {
        setState((prev) => ({ ...prev, onOutsideClick: callback }))
      }
    }),
    [state.eventId, state.preventClose, state.anchorEl]
  )

  Logger.orange('EventPopoverProvider:state', state)

  return (
    <EventPopoverContext.Provider value={value}>
      {children}

      <TFloatingPopover
        open={!!state.anchorEl}
        onClose={value.closePopover}
        anchorElement={state.anchorEl}
        content={state.content}
        defaultPlacement="left"
        closeOnTouchOutside={false} // Pass preventClose to TFloatingPopover
        onOutsideClick={() => {}} // Pass onOutsideClick to TFloatingPopover
      />
    </EventPopoverContext.Provider>
  )
}

export const useEventPopover = () => {
  const context = useContext(EventPopoverContext)
  if (!context) {
    throw new Error('useEventPopover must be used within EventPopoverProvider')
  }
  return context
}
